import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { AUTH_PROFILES } from '@/config/app.config';
import { isTeamProfile } from '@/services/userPermissions';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, Busy } from '@/components/kit';
import { committed } from '@/shared/data';
import { cn } from '@/lib/utils';
import { LABEL, WRAP } from '@/components/home/styles';
import Honeypot from '@/components/Honeypot';
import { useAntiSpam } from '@/shared/antispam';
import { useT } from '@/shared/i18n';
import { H } from '@/shared/locales/home/keys';

const EMPTY_CONTACT = { name: '', email: '', message: '' };

export function Contact() {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const spam = useAntiSpam('contact', { minMs: 3000, cooldownMs: 60000 });

  async function send() {
    const verdict = spam.check();
    if (!verdict.ok) {
      if (verdict.silent) {
        // A bot filled the hidden field: act as if it worked and store nothing.
        setSentTo(form.email.trim());
        setForm(EMPTY_CONTACT);
      } else {
        toast.error(verdict.message);
      }
      return;
    }
    setSending(true);
    try {
      const res = await sdk.table('contact_messages_c').create({
        Name: form.name.trim(),
        email_c: form.email.trim(),
        message_c: form.message.trim(),
        status_c: 'New',
      });
      if (!committed(res)) return;
      spam.sent();
      setSentTo(form.email.trim());
      setForm(EMPTY_CONTACT);
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="contact" className="mx-auto max-w-3xl scroll-mt-24 px-5 pt-40 md:px-10">
      {sentTo ? (
        <div className="text-center">
          <ApperIcon name="Ghost" size={36} className="mx-auto text-primary" />
          <p className="mt-4 text-[clamp(28px,4vw,40px)] font-light">{t(H.received)}</p>
          <p className="mt-3 text-muted-foreground">{t(H.replyTo, { email: sentTo })}</p>
          <Button variant="ghost" className="mt-6" onClick={() => setSentTo('')}>
            {t(H.another)}
          </Button>
        </div>
      ) : (
        <form
          className="relative space-y-8"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <p className={LABEL}>{t('Contact')}</p>
          <Honeypot id="contact-website" {...spam.trapProps} />
          <Field label={t(H.help)} htmlFor="contact-message">
            <Textarea
              id="contact-message"
              required
              rows={3}
              className="resize-none text-lg md:text-xl"
              placeholder={t(H.msgPh)}
              value={form.message}
              onChange={set('message')}
            />
          </Field>
          <div className="grid gap-6 md:grid-cols-2">
            <Field label={t(H.name)} htmlFor="contact-name">
              <Input id="contact-name" required placeholder={t(H.yourName)} value={form.name} onChange={set('name')} />
            </Field>
            <Field label={t(H.email)} htmlFor="contact-email">
              <Input
                id="contact-email"
                required
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={set('email')}
              />
            </Field>
          </div>
          <Button type="submit" size="lg" disabled={sending}>
            <Busy busy={sending} label={t(H.send)} busyLabel={t(H.sending)} />
          </Button>
        </form>
      )}
    </section>
  );
}

export function Portals() {
  const { t } = useT();
  const user = useSelector((s) => s.user.user);
  const portals = AUTH_PROFILES ?? [];
  const team = user && isTeamProfile(user.profile ?? user.accounts?.[0]?.profile?.name);

  return (
    <section className={cn(WRAP, 'pb-8 pt-36')}>
      <div className="rounded-lg border border-border bg-card p-6 text-card-foreground md:p-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className={LABEL}>{user ? t(H.welcome) : t(H.haunted)}</p>
            <h2 className="mt-3 font-heading text-4xl">
              {user ? t(H.oneClick) : t(H.signInGC)}
            </h2>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              {t(H.lives)}
            </p>
            <Link to="/install" className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              <ApperIcon name="Smartphone" size={15} />
              {t(H.keepTap)}
            </Link>
          </div>
          {user ? (
            <Button size="lg" asChild>
              <Link to={team ? '/admin' : '/account'}>{team ? t(H.console) : t(H.goAccount)}</Link>
            </Button>
          ) : (
            <div className="flex flex-wrap gap-3">
              <Button size="lg" asChild>
                <Link to="/customer/signup">{t(H.create)}</Link>
              </Button>
              {portals.map((p) => (
                <Button key={p.key} variant="outline" size="lg" asChild>
                  <Link to={`/${p.key}/login`}>{p.key === 'customer' ? t('Customer sign in') : `${p.label} sign in`}</Link>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
